import { useMemo } from 'react';

import { useCanAccessCommunicationsDashboard, useIsCompactHeaderGeneralEnabled } from '../../featureChecks';
import type { UseMainUIFeatureSettings } from '../../types';
import { uiFeatureAccessSymbol } from '../../types';

import { useCommsDashboardConversationsPageSettings } from './useCommsDashboardConversationsPageSettings';
import { useCommsDashboardDigitalSafetyPageSettings } from './useCommsDashboardDigitalSafetyPageSettings';
import { useCommsDashboardLeadersAndAudiencesPageSettings } from './useCommsDashboardLeadersAndAudiencesPageSettings';

/**
 * @deprecated `@ms/yammer-web-ui-features` is a monolithic package which adds bloat from all feature access settings
 * to a single bootstrap API call (FeatureAccessSettingsClients) and JS assets. DO NOT add new code to this package.
 * Your team is responsible for migrating fields and code out of this package as you move your features to Relay, where
 * your feature should request the necessary `viewerCanXX` fields in its own colocated fragment. Note that we may also
 * need to design a new scalable permissions-check solution for features which require additional logic to check access.
 */
export const useCommsDashboardPageSettings: UseMainUIFeatureSettings<'communicationsDashboardPage'> = (options) => {
  const commsDashboardOverrides = options?.overrides?.communicationsDashboardPage;

  const canAccessDashboard = useCanAccessCommunicationsDashboard();
  const canAccessDigitalSafety = useCommsDashboardDigitalSafetyPageSettings()[uiFeatureAccessSymbol];
  const canAccessLeadersAndAudiences = useCommsDashboardLeadersAndAudiencesPageSettings()[uiFeatureAccessSymbol];
  const isCompactHeaderGeneralEnabled = useIsCompactHeaderGeneralEnabled();
  const canAccessDashboardConversations = useCommsDashboardConversationsPageSettings()[uiFeatureAccessSymbol];

  return useMemo(
    () => ({
      [uiFeatureAccessSymbol]: canAccessDashboard,
      isCompactHeader: isCompactHeaderGeneralEnabled,
      digitalSafety: canAccessDigitalSafety,
      leadersAndAudiences: canAccessLeadersAndAudiences,
      conversations: canAccessDashboardConversations,
      ...commsDashboardOverrides,
    }),
    [
      canAccessDashboard,
      canAccessDashboardConversations,
      canAccessDigitalSafety,
      canAccessLeadersAndAudiences,
      commsDashboardOverrides,
      isCompactHeaderGeneralEnabled,
    ]
  );
};
